import type { ConductionResults } from '@wix/wix-petri-client';
import { anyPass, complement, isEmpty, isNil, omit, pipe, propSatisfies } from 'ramda';
import type { Condition } from '../../types/conditions';
import { everyEvaluate } from '../array';

export const isWithoutConditions = propSatisfies(anyPass([isEmpty, isNil]), 'conditions');
export const isWithConditions = complement(isWithoutConditions);

/**
 * server condition - condition, that can be checked on server (it has `specName`/`specValue` fields).
 * We check only `specName` because one can't exist without another
 * It also can have client checks (devMode, interactionMode, etc)
 */
export const isConditionWithServerChecks = (condition: Condition) => Boolean(condition.specName);

/**
 * condition without client checks, or server only condition - should ONLY be checked on server.
 * it has `specName`/`specValue` fields and no other fields
 */
export const isConditionWithoutClientChecks = (condition: Condition) =>
  pipe(omit(['specName', 'specValue']), isEmpty)(condition);

const EXPERIMENT_FALLBACK_VALUES = ['old', 'false'];

/**
 * returns function, that, given experiments, determines if condition is passing or not
 */
export const evaluateCondition =
  ({ experiments }: { experiments: ConductionResults }) =>
  (condition: Condition): boolean => {
    const conditionsToEvaluate = [];

    // does specName match
    const { specName = '', specValue = '' } = condition;
    if (specName) {
      conditionsToEvaluate.push(() => {
        const specNames = specName.split(',');
        const specValues = specValue.split(',');

        return specNames.every((currentSpecName, index) => {
          const currentSpecValue = specValues[index];
          const experimentValue = experiments[currentSpecName];

          return experimentValue
            ? // checking experiment value when it is conducted and we have its value
              currentSpecValue === experimentValue
            : // fallback values when experiment is not conducted
              EXPERIMENT_FALLBACK_VALUES.includes(currentSpecValue);
        });
      });
    }

    return everyEvaluate(conditionsToEvaluate);
  };
